import React, { useEffect, useState } from 'react';
import { Grid, Card, CardContent, Typography, Box } from '@mui/material';
import { Thermostat, Opacity, FlashOn, Sensors, Straighten, Speed } from '@mui/icons-material';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend
} from 'chart.js';
import CircleProgress from './CircleProgress';
import ExpandableCard from './ExpandableCard';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

// Socket.io evento 'mqttMachine12' (MQTT "Machine12/datos", cada ~10s):
// { temperatura, temperatura2, humedad, amperaje, activaciones_min } y/o { temperatura, distancia, rpm }
const MAX_PUNTOS = 30;        // ~5 min de historial en vivo
const SEGUNDOS_SIN_SENAL = 30; // sin mensajes en este tiempo => "Sin señal"
const API_URL = process.env.REACT_APP_API_URL || 'http://31.97.100.31:5002/api';

const esNumero = (v) => typeof v === 'number' && Number.isFinite(v);

const formatHora = (d) => d
    ? d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
    : '--:--:--';

const TableroMachine12 = ({ socket, theme, darkMode }) => {
    const [ultimo, setUltimo] = useState(null);
    const [ultimaHora, setUltimaHora] = useState(null);
    const [historial, setHistorial] = useState([]);
    const [ahora, setAhora] = useState(Date.now());

    useEffect(() => {
        const agregarLectura = (datos, hora) => {
            setUltimo(datos);
            setUltimaHora(hora);
            setHistorial(prev => [...prev, { hora, ...datos }].slice(-MAX_PUNTOS));
        };

        const handleMachine12 = (data) => agregarLectura(data, new Date());
        socket.on('mqttMachine12', handleMachine12);

        // Último valor conocido por el backend, para no arrancar vacío
        let cancelado = false;
        fetch(`${API_URL}/machine12/estado`)
            .then(r => (r.ok ? r.json() : null))
            .then(estado => {
                if (!cancelado && estado?.ultimoMensaje?.datos) {
                    setUltimo(prev => prev ?? estado.ultimoMensaje.datos);
                    setUltimaHora(prev => prev ?? new Date(estado.ultimoMensaje.recibido));
                }
            })
            .catch(() => {});

        const timer = setInterval(() => setAhora(Date.now()), 5000);

        return () => {
            cancelado = true;
            socket.off('mqttMachine12', handleMachine12);
            clearInterval(timer);
        };
    }, [socket]);

    const enLinea = ultimaHora && (ahora - ultimaHora.getTime()) / 1000 < SEGUNDOS_SIN_SENAL;
    const d = ultimo || {};

    const indicadores = [
        { key: 'temperatura', label: 'Temperatura', icon: Thermostat, color: '#ff8a65', max: 80, texto: v => `${v}°C` },
        { key: 'temperatura2', label: 'Temperatura 2', icon: Thermostat, color: '#e57373', max: 80, texto: v => `${v}°C` },
        { key: 'humedad', label: 'Humedad', icon: Opacity, color: '#4fc3f7', max: 100, texto: v => `${v}%` },
        { key: 'amperaje', label: 'Amperaje', icon: FlashOn, color: '#FFD700', max: 40, texto: v => `${v}A` },
        { key: 'activaciones_min', label: 'Activ./min', icon: Sensors, color: '#81c784', max: 60, texto: v => `${v}` },
        { key: 'distancia', label: 'Distancia', icon: Straighten, color: '#ba68c8', max: 400, texto: v => (v < 0 ? '--' : `${v}cm`) },
        { key: 'rpm', label: 'RPM', icon: Speed, color: '#B8F1FF', max: 60, texto: v => `${v}` },
    ];
    // Distancia y RPM solo aparecen si el firmware las envía
    const visibles = indicadores.filter(i => !['distancia', 'rpm'].includes(i.key) || esNumero(d[i.key]));

    const seriesGrafica = indicadores.filter(i => ['temperatura', 'temperatura2', 'humedad', 'amperaje'].includes(i.key));
    const chartData = {
        labels: historial.map(h => formatHora(h.hora)),
        datasets: seriesGrafica.map(s => ({
            label: s.label,
            data: historial.map(h => (esNumero(h[s.key]) ? h[s.key] : null)),
            borderColor: s.color,
            backgroundColor: s.color,
            borderWidth: 2,
            pointRadius: 2,
            tension: 0.3,
        })),
    };
    const gridColor = darkMode ? 'rgba(60, 120, 200, 0.15)' : 'rgba(0,0,0,0.08)';
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: {
            legend: { labels: { color: theme.textSub, boxWidth: 10, font: { size: 10 } } },
        },
        scales: {
            x: { ticks: { color: theme.textMuted, font: { size: 9 }, maxTicksLimit: 6 }, grid: { color: gridColor } },
            y: { ticks: { color: theme.textMuted, font: { size: 9 } }, grid: { color: gridColor }, beginAtZero: true },
        },
    };

    const panelSx = {
        border: `1px solid ${theme.borderCard}`,
        borderRadius: '6px',
        backgroundColor: theme.cardBg2,
        boxShadow: theme.shadowInset,
    };

    return (
        <Grid container spacing={1} sx={{ mt: 1, mb: 2 }}>
            <Grid item xs={12}>
                <ExpandableCard>
                    <Card sx={{ backgroundColor: theme.cardBg, color: theme.text, boxShadow: theme.shadow, border: `1px solid ${theme.borderCard}`, borderRadius: '10px' }}>
                        <CardContent sx={{ p: { xs: 0.5, sm: 2 }, pb: { xs: 0.5, sm: 2 } }}>
                            <Typography variant="h7" component="div" sx={{ borderBottom: `1px solid ${theme.border}`, pb: 1, mb: 1, color: theme.text, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                                <Box component="span" sx={{ fontSize: { xs: '0.85rem', sm: '1rem' }, fontWeight: 'bold' }}>Machine12 · Sensores</Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: enLinea ? '#4caf50' : '#f44336', boxShadow: enLinea ? '0 0 6px #4caf50' : 'none' }} />
                                    <Box component="span" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, color: theme.textSub }}>
                                        {enLinea ? 'En línea' : 'Sin señal'} · última {formatHora(ultimaHora)}
                                    </Box>
                                </Box>
                            </Typography>
                            <Grid container spacing={1} alignItems="stretch">
                                <Grid item xs={12} md={5}>
                                    <Grid container spacing={1}>
                                        {visibles.map(({ key, label, icon: Icon, color, max, texto }) => {
                                            const valor = esNumero(d[key]) ? d[key] : null;
                                            return (
                                                <Grid item xs={6} sm={4} md={6} key={key}>
                                                    <Box sx={{ ...panelSx, height: { xs: 80, sm: 96 }, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5, p: 0.75 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Icon sx={{ color: valor !== null ? color : theme.textMuted, fontSize: { xs: 18, sm: 20 } }} />
                                                            <CircleProgress
                                                                value={Math.max(valor ?? 0, 0)}
                                                                min={0}
                                                                max={max}
                                                                size={50}
                                                                strokeWidth={4}
                                                                bgColor="transparent"
                                                                textColor={theme.text}
                                                                progressColor={valor !== null ? color : 'transparent'}
                                                                displayText={valor !== null ? texto(valor) : '-'}
                                                            />
                                                        </Box>
                                                        <Typography sx={{ color: theme.textMuted, fontSize: 9, lineHeight: 1.4 }}>{label}</Typography>
                                                    </Box>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </Grid>
                                <Grid item xs={12} md={7}>
                                    <Box sx={{ ...panelSx, borderRadius: '8px', height: { xs: 220, md: '100%' }, minHeight: 220, p: 1, position: 'relative' }}>
                                        {historial.length > 0 ? (
                                            <Line data={chartData} options={chartOptions} />
                                        ) : (
                                            <Box sx={{ height: '100%', minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <Typography sx={{ color: theme.textMuted, fontSize: '0.8rem' }}>Esperando datos en tiempo real…</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </ExpandableCard>
            </Grid>
        </Grid>
    );
};

export default TableroMachine12;
